import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Search } from 'src/@core/interfaces/search';
import { JobService } from 'src/@core/services/job/job.service';
import { RolesPermissionsService } from 'src/app/modules/roles-permissions/roles-permissions.service';
import { AssignedLeadUser } from '../models/entity-leads.types';
import { getTeamMemberDisplayName, getTeamMemberUserId } from '../utils/team-member.util';

const USER_PAGE_SIZE = 10;
const ROLE_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Pick users by role: a role first, then users holding it, searched and paged on
 * the server as you type. In `multiple` mode picks accumulate as chips (and can
 * span roles); otherwise picking a user replaces the previous one.
 */
@Component({
  selector: 'hour4u-admin-role-user-picker',
  templateUrl: './role-user-picker.component.html',
  styleUrls: ['./role-user-picker.component.scss']
})
export class RoleUserPickerComponent implements OnDestroy {
  @Input() entityId: string;
  @Input() multiple = false;
  @Input() selectedUsers: AssignedLeadUser[] = [];
  @Input() disabled = false;

  @Output() selectedUsersChange = new EventEmitter<AssignedLeadUser[]>();

  roles: string[] = [];
  selectedRole: string | null = null;
  isLoadingRoles = false;
  private rolesLoaded = false;
  isRoleOpen = false;

  users: any[] = [];
  userSearch = '';
  isLoadingUsers = false;
  isUserOpen = false;
  private userPage = 0;
  private userTotalPages = 0;

  private searchTimeout: any;
  private usersSub: Subscription | null = null;

  constructor(
    private rolesPermissionsService: RolesPermissionsService,
    private jobService: JobService,
    private elementRef: ElementRef,
  ) { }

  ngOnDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.usersSub) {
      this.usersSub.unsubscribe();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if ((this.isRoleOpen || this.isUserOpen) && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isRoleOpen = false;
      this.isUserOpen = false;
    }
  }

  get roleLabel(): string {
    return this.selectedRole || 'Select role';
  }

  get userLabel(): string {
    if (!this.selectedRole) {
      return 'Select a role first';
    }
    if (this.multiple) {
      return 'Add users';
    }
    const picked = this.selectedUsers && this.selectedUsers[0];
    return picked ? picked.user_name || 'Selected user' : 'Select user';
  }

  get userPageNumber(): number {
    return this.userPage + 1;
  }

  get userTotalPageCount(): number {
    return Math.max(1, this.userTotalPages);
  }

  toggleRoleDropdown(event: Event) {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.isUserOpen = false;
    this.isRoleOpen = !this.isRoleOpen;
    // Loaded on first open: a workflow renders one picker per outcome, and most
    // of them are never touched.
    if (this.isRoleOpen && !this.rolesLoaded && !this.isLoadingRoles) {
      this.fetchRoles();
    }
  }

  selectRole(role: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isRoleOpen = false;
    if (role === this.selectedRole) {
      return;
    }
    this.selectedRole = role;
    this.userSearch = '';
    this.users = [];
    this.userTotalPages = 0;
    this.fetchUsers(0);
    // Straight on to the users: picking a role is only ever the first half.
    this.isUserOpen = true;
  }

  toggleUserDropdown(event: Event) {
    event.stopPropagation();
    if (this.disabled || !this.selectedRole) {
      return;
    }
    this.isRoleOpen = false;
    this.isUserOpen = !this.isUserOpen;
    if (this.isUserOpen && !this.users.length && !this.isLoadingUsers) {
      this.fetchUsers(0);
    }
  }

  onUserSearchChange(value: string) {
    this.userSearch = value;
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => this.fetchUsers(0), SEARCH_DEBOUNCE_MS);
  }

  goToUserPage(page: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (page < 0 || page >= this.userTotalPages || page === this.userPage || this.isLoadingUsers) {
      return;
    }
    this.fetchUsers(page);
  }

  getUserName(member: any): string {
    return getTeamMemberDisplayName(member);
  }

  getUserEmail(member: any): string {
    return (member && member.user && member.user.email) || '';
  }

  isUserSelected(member: any): boolean {
    const userId = getTeamMemberUserId(member);
    return !!userId && (this.selectedUsers || []).some((user) => user.user_id === userId);
  }

  toggleUser(member: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    const userId = getTeamMemberUserId(member);
    if (!userId) {
      return;
    }
    const current = this.selectedUsers || [];
    if (!this.multiple) {
      this.isUserOpen = false;
      if (current.length === 1 && current[0].user_id === userId) {
        return;
      }
      this.emit([{ user_id: userId, user_name: this.getUserName(member) }]);
      return;
    }
    if (this.isUserSelected(member)) {
      this.emit(current.filter((user) => user.user_id !== userId));
    } else {
      this.emit([...current, { user_id: userId, user_name: this.getUserName(member) }]);
    }
  }

  removeUser(userId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (this.disabled) {
      return;
    }
    this.emit((this.selectedUsers || []).filter((user) => user.user_id !== userId));
  }

  trackByUserId(index: number, member: any): string {
    return getTeamMemberUserId(member) || String(index);
  }

  private emit(users: AssignedLeadUser[]) {
    this.selectedUsers = users;
    this.selectedUsersChange.emit(users);
  }

  private fetchRoles() {
    this.isLoadingRoles = true;
    this.rolesPermissionsService.fetchRolesWithPermissions('', 0, ROLE_PAGE_SIZE, 'createdOn', 'desc').subscribe(
      (res: any) => {
        const names = ((res && res.content) || [])
          .map((role: any) => role && role.roleName)
          .filter((name: any) => typeof name === 'string' && name.trim());
        this.roles = Array.from(new Set<string>(names));
        this.rolesLoaded = true;
        this.isLoadingRoles = false;
      },
      () => {
        this.roles = [];
        this.isLoadingRoles = false;
      }
    );
  }

  private fetchUsers(page: number) {
    if (!this.entityId || !this.selectedRole) {
      return;
    }
    if (this.usersSub) {
      // A newer keystroke or page wins; a slow earlier reply must not overwrite it.
      this.usersSub.unsubscribe();
    }
    const search = [new Search('roleName', this.selectedRole)];
    const query = (this.userSearch || '').trim();
    if (query) {
      search.push(new Search('query', query));
    }
    this.isLoadingUsers = true;
    this.usersSub = this.jobService.searchAndSortTeamMembersv2(
      this.entityId, page, USER_PAGE_SIZE, 'createdOn', 'desc', search
    ).subscribe(
      (res: any) => {
        this.users = (res && res.content) || [];
        this.userPage = page;
        this.userTotalPages = (res && res.totalPages) || 0;
        this.isLoadingUsers = false;
      },
      () => {
        this.users = [];
        this.userTotalPages = 0;
        this.isLoadingUsers = false;
      }
    );
  }
}
